<template>
  <FlotoCrudContainer
    ref="list"
    as-table
    :scrollable="searchable || scrollable"
    :module-name="moduleName"
    :immediate="!searchable"
    :fetch-fn="fetchTicketFn"
    :columns="tableColumns"
    :selectable="selectable"
    v-bind="$attrs"
    @selection-change="$emit('selection-change', $event)"
  >
    <template v-slot:form>
      <span />
    </template>
    <template v-slot:name="{ item }">
      <td>
        <ResourceListTitle
          :name="item.name"
          external-link
          internal-link
          :link="
            !item.archived
              ? $modules.getModuleRoute('software-license', 'view', {
                  params: { id: item.id },
                })
              : undefined
          "
        />
      </td>
    </template>
    <template v-slot:displayName="{ item }">
      <td class="text-ellipsis">{{ item.displayName }}</td>
    </template>
    <template v-slot:productId="{ item }">
      <td class="text-ellipsis">{{ productName(item) }}</td>
    </template>
    <template v-slot:licenceTypeId="{ item }">
      <td class="text-ellipsis">
        <SoftwareLicenseTypePicker :value="item.licenceTypeId" disabled />
      </td>
    </template>
    <template v-slot:expiryDate="{ item }">
      <td class="text-ellipsis">
        <span v-if="item.expiryDate">
          {{ item.expiryDate | datetime(undefined, false) }}
        </span>
        <span v-else>---</span>
      </td>
    </template>
    <template v-slot:actions="{ item }">
      <td
        :class="{
          'sticky-action': actionSticked.indexOf(item.id) >= 0,
          'hover-action-container': hoverableActions,
        }"
      >
        <slot
          name="action"
          :item="item"
          :stick-action="() => stickActionFor(item)"
          :unstick-action="() => unStickActionFor(item)"
        />
      </td>
    </template>
    <template v-slot="slotData">
      <slot v-bind="slotData"></slot>
    </template>
  </FlotoCrudContainer>
</template>

<script>
import Uniq from 'lodash/uniq'
import {
  // transformSearchCriteriasForServer,
  transformSearchCriteriaForServer,
} from '@data/search'
import { searchByQualApi } from '@modules/software-license/software-license-api'
import ResourceListTitle from '@components/resource/resource-list-title'
import { getProductCatalogsApi } from '@modules/asset-management/api/product-catalog-api'
import SoftwareLicenseTypePicker from '@components/data-picker/software-license-type-picker'

export default {
  name: 'SoftwareLicenseList',
  components: { ResourceListTitle, SoftwareLicenseTypePicker },
  props: {
    moduleName: {
      type: String,
      default() {
        return this.$constants.SOFTWARE_LICENSE
      },
    },
    fetchFn: { type: Function, default: undefined },
    hasActions: { type: Boolean, default: false },
    searchable: { type: Boolean, default: false },
    scrollable: { type: Boolean, default: false },
    selectable: { type: Boolean, default: false },
    softwareAssetId: { type: Number, default: undefined },
    hiddenColumns: {
      type: Array,
      default() {
        return ['renewalDate']
      },
    },
    excludeItemsById: {
      type: Array,
      default() {
        return []
      },
    },
    hoverableActions: { type: Boolean, default: false },
  },
  data() {
    this.tableColumns = [
      { name: `${this.$tc('id')}`, key: 'name', maxWidth: '150px' },
      { name: `${this.$tc('name')}`, key: 'displayName', width: '20%' },
      { name: `${this.$tc('product')}`, key: 'productId' },
      { name: `${this.$tc('license_type')}`, key: 'licenceTypeId' },
      {
        name: `${this.$tc('purchase')} ${this.$tc('count')}`,
        key: 'purchaseCount',
      },
      {
        name: `${this.$tc('allocation')} ${this.$tc('count')}`,
        key: 'allocationCount',
      },
      {
        name: `${this.$tc('installation')} ${this.$tc('count')}`,
        key: 'totalInstallation',
      },
      {
        name: `${this.$tc('expiry')} ${this.$tc('date')}`,
        key: 'expiryDate',
      },
      ...(this.hasActions
        ? [
            {
              name: this.$tc('action', 2),
              key: 'actions',
              width: '80px',
            },
          ]
        : []),
    ]
    return {
      actionSticked: [],
      productList: [],
      currentSelectedSearch: null,
    }
  },
  methods: {
    fetchTicketFn(limit, offset) {
      var criterias = { quals: [] }
      if (this.excludeItemsById.length) {
        const q = transformSearchCriteriaForServer(this.moduleName, {
          operator: { value: 'not_in' },
          paramName: 'id',
          value: this.excludeItemsById,
          valueType: 'long',
        })
        criterias.quals.push(q)
      }
      if (this.softwareAssetId) {
        const q = transformSearchCriteriaForServer(this.moduleName, {
          operator: { value: 'equal' },
          paramName: 'softwareAssetId',
          value: this.softwareAssetId,
          valueType: 'long',
        })
        criterias.quals.push(q)
        criterias = {
          ...criterias,
          type: 'BatchQualificationRest',
        }
      }
      if (this.fetchFn) {
        return this.fetchFn(criterias, limit, offset).then((response) => {
          const productIds = response.items.map((i) => i.productId)
          if (productIds.length) {
            this.getProducts(productIds)
          }
          return response
        })
      } else {
        return searchByQualApi(criterias, limit, offset).then((response) => {
          const productIds = response.items.map((i) => i.productId)
          if (productIds.length) {
            this.getProducts(productIds)
          }
          return response
        })
      }
    },
    getProducts(ids) {
      getProductCatalogsApi({ ids }).then((data) => {
        this.productList = data.items
      })
    },
    productName(item) {
      if (item.productId) {
        const product = this.productList.find((v) => v.id === item.productId)
        return product ? `${product.name} ${product.displayName}` : '---'
      }
      return '---'
    },
    refresh() {
      this.$refs.list.refresh()
    },
    stickActionFor(item) {
      this.actionSticked = Uniq([...this.actionSticked, item.id])
    },
    unStickActionFor(item) {
      this.actionSticked = this.actionSticked.filter((id) => id !== item.id)
    },
  },
}
</script>
